'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import { useEffect, useState } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { Banner } from './banner';

const SLIDES = [
  { id: 3, image: '/images/hero/deti.png' },
  { id: 2, image: '/images/hero/muzi.png' },
  { id: 1, image: '/images/hero/zena.png' },
];

export function Hero() {
  return (
    <section className={cn('custom-section', 'relative bg-[#FAF6F2] pt-6 lg:pt-24 xl:pt-6')}>
      <Circle className={cn('-left-10 top-4 sm:left-0 md:left-44 lg:top-[74px]')} />
      <HeroSlides />
      <Image
        src="/images/blog-bottom.svg"
        alt="Úvodná sekcia"
        width={0}
        height={0}
        className="absolute -bottom-0 left-0 z-10 h-auto w-full object-contain"
      />
    </section>
  );
}

function HeroSlides() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActive(prev => (prev + 1) % SLIDES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className={cn(
        'custom-container--xxl relative flex flex-col items-center justify-center gap-0',
        'lg:flex-row lg:items-start lg:justify-end lg:gap-12 xl:pb-[20px]',
      )}
    >
      <HeroLeftContent />
      <div className="relative flex items-center justify-center">
        <Image
          src="/images/big-circle.svg"
          alt=""
          width={0}
          height={0}
          className="aspect-square w-full min-w-[420px] max-w-[420px] translate-y-24 
          sm:min-w-[600px] sm:max-w-[440px] 
          md:min-w-[650px] md:max-w-[650px]
          lg:min-w-[500px] lg:max-w-[600px]
          xl:min-w-[783px] xl:max-w-[783px]"
        />
        <div className="absolute inset-0 flex items-end justify-center overflow-hidden">
          {SLIDES.map((slide, index) => (
            <FadeSlide
              key={slide.id}
              src={slide.image}
              active={active === index}
              priority={index === 0}
            />
          ))}
        </div>
        <div className="absolute -bottom-28 left-1/2 z-[30] w-full max-w-[92vw] -translate-x-1/2 sm:max-w-[800px] md:-bottom-28 lg:-bottom-16 lg:max-w-[486px] xl:-bottom-16">
          <Banner isSmall />
        </div>
      </div>
    </div>
  );
}

function FadeSlide({ src, active, priority }: { src: string; active: boolean; priority: boolean }) {
  return (
    <Image
      src={src}
      alt="Slide"
      width={0}
      height={0}
      sizes="100vw"
      priority={priority}
      fetchPriority={priority ? 'high' : undefined}
      className={cn(
        'absolute bottom-0 left-1/2 h-auto -translate-x-1/2 object-contain',
        'min-w-[440px] max-w-[440px]',
        'sm:min-w-[600px] sm:max-w-[600px]',
        'md:min-w-[700px] md:max-w-[700px]',
        'lg:min-w-[640px] lg:max-w-[640px]',
        'xl:min-w-[820px] xl:max-w-[820px]',
        'ease-[cubic-bezier(0.4,0,0.2,1)] transition-opacity duration-700',
        active ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
    />
  );
}

function HeroLeftContent() {
  const { t } = useTranslations();

  return (
    <div className="flex w-full flex-col items-start justify-start gap-16 lg:max-w-[588px] xl:gap-[130px]">
      <div className="flex w-full flex-col items-start justify-start gap-6 xl:pt-32">
        <Heading
          title="<span>Krása,</span> ktorá vydrží. <span>Štýl,</span> ktorý hovorí za Vás."
          className={{ title: 'w-full max-w-[450px] text-balance lg:max-w-[600px]' }}
          tag="h1"
        />

        <div className="flex items-center gap-2.5">
          <Image
            src="/images/pin.svg"
            alt="Lokalita"
            width={20}
            height={20}
            className="max-h-5 min-h-5 min-w-5 max-w-5 object-contain"
          />
          <p className="text-sm font-medium text-black sm:text-base lg:text-lg">
            Tomášikova 8/A, 080 01 Sídlisko III (OC Centrum)
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="dark">
            <Link prefetch={false} href="/rezervacia">
              <Image
                src="/images/calendar.svg"
                alt=""
                width={0}
                height={0}
                className="max-h-5 min-h-5 min-w-5 max-w-5 object-contain"
              />
              {t('text4069')}
            </Link>
          </Button>

          <Button asChild variant="white">
            <Link prefetch={false} href="#sluzby">
              Naše služby
            </Link>
          </Button>
        </div>
      </div>

      <Link
        prefetch={false}
        href="#preco-my"
        className="hidden cursor-pointer items-center gap-2 text-sm font-medium text-[#707070] sm:text-base lg:flex"
      >
        <Image src="/images/arrow-down.svg" alt="" width={20} height={20} />
        Zistite viac
      </Link>
    </div>
  );
}

function Circle({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'absolute aspect-square w-full max-w-[118px] rounded-full opacity-15 sm:max-w-[140px] md:max-w-[210px] lg:max-w-[284px]',
        'ea-hero-circle',
        className,
      )}
    />
  );
}
